import VerticalLayout from "../../../../../lib/html5-software-library/src/ranj/components/layouts/VerticalLayout";
import ScrollPane from "../../../../../lib/html5-software-library/src/ranj/components/scrolling/ScrollPane";
import Rectangle from "../../../../../lib/html5-software-library/src/ranj/geom/Rectangle";
import RevisableLanguagePack from "../../../../../lib/html5-software-library/src/ranj/language/RevisableLanguagePack";
import LayoutManager from "../../../../../lib/html5-software-library/src/ranj/layout/LayoutManager";
import HubEvent from "../../../app/hub/event/HubEvent";
import MediatorManager from "../../../core/application/MediatorManager";
import AssetLibrary from "../../../core/assetloading/AssetLibrary";
import AssetLoadService from "../../../core/assetloading/AssetLoadService";
import AbstractMediator from "../../../core/ui/AbstractMediator";
import SkinFactory from "../../../core/ui/SkinFactory";
import NGTVariableManager from "../../base/NGTVariableManager";
import LaptopNavigateEvent from "../../laptop/model/LaptopNavigateEvent";
import NPCModel from "../../scenario/NPCModel";
import SmartphoneSoundType from "../../smartphone/view/SmartphoneSoundType";
import ChatMessageFactory from "../controller/ChatMessageFactory";
import ChatInteractionEvent from "../events/ChatInteractionEvent";
import ChatNpcMessageEvent from "../events/ChatNpcMessageEvent";
import ChatPlayerMessageEvent from "../events/ChatPlayerMessageEvent";
import ChatMessage from "../model/ChatMessage";
import ChatModel from "../model/ChatModel";
import ChatStatus from "../model/ChatStatus";
import ChatSoundType from "../sound/ChatSoundType";
import ChatMessageMediator from "./ChatMessageMediator";
import ChatTimeIndicatorMediator from "./ChatTimeIndicatorMediator";

export default class ChatScreenMediator extends AbstractMediator {
    /**
     * @constructor
     * @param {createjs.MovieClip} target 
     * @param {soma.EventDispatcher} dispatcher 
     * @param {LayoutManager} layoutManager 
     * @param {RevisableLanguagePack} languagePack 
     * @param {ChatModel} chatModel
     * @param {NPCModel} npcModel
     * @param {AssetLibrary} assetLibrary 
     * @param {AssetLoadService} assetLoadService 
     * @param {ChatMessageFactory} chatMessageFactory 
     * @param {MediatorManager} mediatorManager 
     * @param {SoundManager} soundManager 
     * @param {NGTVariableManager} variableManager 
     */
    constructor(target, dispatcher, layoutManager, languagePack, chatModel, npcModel, assetLibrary, assetLoadService,
        chatMessageFactory, mediatorManager, soundManager, variableManager, keyDecisionsConfig) {

        super(target, dispatcher, layoutManager);

        this._languagePack = languagePack;
        this._chatModel = chatModel;
        this._npcModel = npcModel;
        this._assetLibrary = assetLibrary;
        this._assetLoadService = assetLoadService;
        this._chatMessageFactory = chatMessageFactory;
        this._mediatorManager = mediatorManager;
        this._soundManager = soundManager;
        this._variableManager = variableManager;
        this._keyDecisionsConfig = keyDecisionsConfig;

        this._npcId = this.getCustomData().npcId;
        this._skinKeyRegistry = this.getCustomData().skinKeyRegistry;

        this._npc = this._npcModel.getNpcByNpcID(this._npcId);
        this._conversation = this._chatModel.getConversationForNpcId(this._npcId);

        /** @type {Array<ChatTimeIndicatorMediator>} */
        this._timeIndicatorMediators = [];
    }

    initialize() {
        super.initialize();
        this.hideKeyDecisionLogo();

        this._initializeTopBar();

        this._initialScrollPaneMask = SkinFactory.getSkin(this._skinKeyRegistry.CHAT_SCROLL_PANE_MASK);
        this._target.scrollPane.addChild(this._initialScrollPaneMask);
        this._initialScrollPaneMask.visible = false;

        const scrollPaneBounds = this._target.scrollPane.nominalBounds;
        const viewport = new Rectangle(0, 0, scrollPaneBounds.width, scrollPaneBounds.height);
        this._initialViewport = viewport;
        const halvedScrollHandleHeight = this._target.scrollBar.dragButton.nominalBounds.height * .5;
        this._scrollPane = new ScrollPane(viewport, null, this._target.scrollBar, this._target.scrollPane.messageContentContainer, null, true, true, halvedScrollHandleHeight);

        this._messagesContainer = new VerticalLayout(this._target.scrollPane.messageContentContainer, 5);
        this._initializeMessages();

        this._target.optionsPanel.customData = {
            npcId: this._npcId
        };
        this._optionsPanelMediator = this._mediatorManager.createMediator(this._target.optionsPanel, this._skinKeyRegistry.CHAT_OPTIONS_PANEL_SKIN);

        this.registerEventListener(ChatPlayerMessageEvent.SENT, this._handlePlayerMessageSent.bind(this));
        this.registerEventListener(ChatNpcMessageEvent.SENT, this._handleNpcMessageSent.bind(this));
        this.registerEventListener(ChatInteractionEvent.CHAT_STARTED, this._handleChatStarted.bind(this));
        this.registerEventListener(ChatInteractionEvent.CHAT_ENDED, this._handleChatEnded.bind(this));
        this.registerEventListener(ChatInteractionEvent.CHAT_STATUS_CHANGED, this._updateStatus.bind(this));
        this.registerEventListener(ChatInteractionEvent.NEW_PLAYER_OPTIONS_AVAILABLE, this._handleOptionsAvailable.bind(this));

        this._updateScrollPane();


        // Set a variable on chat opened so that we can act upon this in the story
        if (this._conversation.dialogIdHistory !== undefined) {
            for (let i = 0; i < this._conversation.dialogIdHistory.length; i++) {
                const dialogId = this._conversation.dialogIdHistory[i];
                if (this._variableManager.getVariable(`chat_${dialogId}_viewed`) === undefined || !this._variableManager.getVariable(`chat_${dialogId}_viewed`)) {
                    if (this._keyDecisionsConfig.contentIdUnlocksKeyDecision(dialogId)) {
                        this.showKeyDecisionLogo();
                    }
                    this._variableManager.setVariable(`chat_${dialogId}_viewed`, true);
                }
            }
        }
    }

    destroy() {
        super.destroy();

        this._optionsPanelMediator.destroy();

        for (let i = 0; i < this._chatMessageMediators.length; i++) {
            const chatMessageMediator = this._chatMessageMediators[i];
            this._messagesContainer.removeItem(chatMessageMediator.skin);
            chatMessageMediator.destroy();
        }

        this._scrollPane.setScrollPosition(0, 0);
        this._scrollPane.destroy();
    }

    hideKeyDecisionLogo() {
        this._target.NewKeyDecisionFound.alpha = 0;
    }

    showKeyDecisionLogo() {
        this.dispatch(LaptopNavigateEvent.HIDE_OVERLAY);
        this._target.NewKeyDecisionFound.alpha = 1;
        this._target.NewKeyDecisionFound.timeline.loop = 0;
        this._target.NewKeyDecisionFound.timeline.gotoAndPlay(0);
        this._target.NewKeyDecisionFound.timeline.on('change', (evt) => {
            if (this._target.NewKeyDecisionFound.timeline.paused) {
                evt.remove();
                //this.dispatch(HubEvent.REFRESH_KEY_DECISIONS_LIST);
            }
        });
    }

    _initializeTopBar() {
        this._target.topInfo.nameTextField.text = this._npc.getName() || this._npc.getNickname();
        this._target.topInfo.titleTextField.text = this._npc.getRole();

        this._updateStatus();

        const npcImage = this._npc.getImage();
        if (npcImage) {
            this._loadAndShowImage(`${npcImage}_small`, `assets/images/npcs/${npcImage}_small.png`);
        }
    }

    _initializeMessages() {
        if (this._chatMessageMediators) {
            console.warn(`Chat messages are already initialized. Skipping initialization...`);
            return;
        }

        /** @type {Array<ChatMessageMediator>} */
        this._chatMessageMediators = [];

        for (let i = 0; i < this._conversation.messages.length; i++) {
            const chatMessage = this._conversation.messages[i];
            this._showMessage(chatMessage);
        }
    }
    /**
     * Adds a chat message on the screen
     * @param {ChatMessage} chatMessage 
     */
    _showMessage(chatMessage) {

        if (!this._chatMessageMediators) {
            console.warn(`Chat messages are not yet initialized. Initializing first...`);
            this._initializeMessages();
        }

        // New message(s) indicator line
        if (!chatMessage.isRead && !this._newLine) {
            this._newLine = SkinFactory.getSkin(this._skinKeyRegistry.CHAT_NEW_LINE)
            this._newLine.textField.text = this._languagePack.getText(`chat.newLineLabel`);
            this._messagesContainer.addItem(this._newLine);
        }

        // Time indicator line
        const lastMessage = this._chatMessageMediators.length > 0 ? this._chatMessageMediators[this._chatMessageMediators.length - 1].model : null;
        if (lastMessage) {
            const minimalDeltaForShowingTimeLine = 0;
            const timeDelta = chatMessage.time - lastMessage.time;
            if (timeDelta > minimalDeltaForShowingTimeLine) {
                // Update other time indicators as time might have passed when this screen is open
                this._updateChatTimeIndicators();

                this._createChatTimeIndicatorLine(chatMessage.time);
            }
        } else {
            // This is the first message in the conversation, which will always have a time indicator
            this._createChatTimeIndicatorLine(chatMessage.time);
        }

        chatMessage.markAsRead();

        const chatMessageMediator = this._chatMessageFactory.create(chatMessage, this._skinKeyRegistry);

        this._messagesContainer.addItem(chatMessageMediator.skin);
        this._chatMessageMediators.push(chatMessageMediator);

        this._updateScrollPane();
    }

    _createChatTimeIndicatorLine(time) {
        const timeIndicatorSkin = SkinFactory.getSkin(this._skinKeyRegistry.CHAT_DATE_LINE);
        this._messagesContainer.addItem(timeIndicatorSkin);

        const timeIndicatorMediator = this._mediatorManager.createMediator(timeIndicatorSkin, this._skinKeyRegistry.CHAT_TIME_INDICATOR_SKIN);
        timeIndicatorMediator.setTime(time);
        this._timeIndicatorMediators.push(timeIndicatorMediator);
    }

    _updateChatTimeIndicators() {
        for (let i = 0; i < this._timeIndicatorMediators.length; i++) {
            const timeIndicatorMediator = this._timeIndicatorMediators[i];
            timeIndicatorMediator.update();
        }
    }

    _handlePlayerMessageSent(evt) {
        const data = evt.params.data;
        if (data.conversation !== this._conversation) {
            return;
        }
        this.playSound(ChatSoundType.TEXT_MESSAGE);

        const message = data.chatMessage;
        this._showMessage(message);
    }

    _handleNpcMessageSent(evt) {
        const data = evt.params.data;
        if (data.conversation !== this._conversation) {
            return;
        }
        this.playSound(SmartphoneSoundType.NEW_CONTENT);

        const message = data.chatMessage;
        this._showMessage(message);
    }

    _loadAndShowImage(id, url) {
        if (this._assetLibrary.hasAsset(id)) {
            this._onImageLoaded(id, false);
            return;
        }

        this._assetLoadService.lazyLoadImage(id, url, this._onImageLoaded.bind(this));
    }

    _onImageLoaded(assetId, animated = true) {
        const image = this._assetLibrary.getAsset(assetId);
        const bitmap = new createjs.Bitmap(image);
        this._target.topInfo.imageContainer.addChild(bitmap);

        if (animated) {
            bitmap.alpha = 0;

            createjs.Tween.get(bitmap)
                .to({ alpha: 1 }, 150, createjs.Ease.sineInOut)
                .call(() => {
                    this._target.topInfo.noPortraitThumb.visible = false;
                });
        } else {
            this._target.topInfo.noPortraitThumb.visible = false;
        }
    }

    _handleChatStarted() {
        this._target.topInfo.statusTextField.text = this._languagePack.getText(`chat.status.available`);
        this._target.topInfo.statusIndicator.gotoAndStop('online');
    }

    _handleChatEnded() {
        this._target.topInfo.statusTextField.text = this._languagePack.getText(`chat.status.unavailable`);
        this._target.topInfo.statusIndicator.gotoAndStop('offline');
    }

    _handleOptionsAvailable(evt) {
        const data = evt.params.data;
        if (data.conversation !== this._conversation) {
            return;
        }

        this._optionsPanelMediator.show();
        this._updateScrollPane();
    }

    _updateStatus() {
        switch (this._conversation.status) {
            case ChatStatus.ONLINE:
                this._target.topInfo.statusTextField.text = this._languagePack.getText(`chat.status.available`);
                this._target.topInfo.statusIndicator.gotoAndStop('online');
                break;
            case ChatStatus.TYPING:
                this._target.topInfo.statusTextField.text = this._languagePack.getText(`chat.status.isTyping`);
                this._target.topInfo.statusIndicator.gotoAndStop('online');
                break;
            case ChatStatus.OFFLINE:
                this._target.topInfo.statusTextField.text = this._languagePack.getText(`chat.status.unavailable`);
                this._target.topInfo.statusIndicator.gotoAndStop('offline');
                break;
            default:
                console.warn(`Unhandled status.`);
        }
    }

    _updateScrollPane() {
        let optionPanelHeight = 0;
        if (this._optionsPanelMediator && this._optionsPanelMediator.hasAvailableOptions()) {
            optionPanelHeight = this._optionsPanelMediator.getHeight();
        }

        this._scrollPane.update();

        const contentBounds = this._messagesContainer.getBounds();
        const contentHeight = contentBounds ? contentBounds.height + 10 : 0;
        this._scrollPane.setHeightBound(contentHeight);

        if (this._optionsPanelMediator && this._optionsPanelMediator.hasAvailableOptions()) {
            this._scrollPane.setViewPort(
                new createjs.Rectangle(0, 0, this._initialViewport.width, this._initialViewport.height - optionPanelHeight + 15));
        } else {
            this._scrollPane.setViewPort(this._initialViewport);

            this._target.scrollPane.messageContentContainer.mask = this._initialScrollPaneMask.children[0];
        }

        if(this._newLine != undefined) {
            // If there is a new line, we scroll to the new line
            this._scrollPane.setScrollPosition(0, -this._newLine.y);
        } else {
            this._scrollPane.setScrollPosition(0, -contentHeight);
        }
    }
}